import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, Download, Plus, Calculator, Sparkles, FileSignature, FileSpreadsheet } from "lucide-react";
import GenerateBulletinDialog from "@/components/GenerateBulletinDialog";
import BulletinsHistoryChart from "@/components/BulletinsHistoryChart";

const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

export default function ChefPaie() {
  const [items, setItems] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [open, setOpen] = useState(false);
  const [salarie, setSalarie] = useState("");
  const [mois, setMois] = useState(new Date().getMonth() + 1);
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [brut, setBrut] = useState("");
  const [brutAutoFilled, setBrutAutoFilled] = useState(false);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historySalarie, setHistorySalarie] = useState("all");

  // Auto-fetch preview + pre-fill brut when salarie + mois + annee changes
  useEffect(() => {
    if (!salarie || !open) { setPreview(null); return; }
    setPreviewLoading(true);
    api.get("/chef/paie/preview", { params: { salarie_id: salarie, mois, annee } })
      .then((r) => {
        setPreview(r.data);
        setBrut(r.data.brut_calcule.toFixed(2));
        setBrutAutoFilled(true);
      })
      .catch(() => setPreview(null))
      .finally(() => setPreviewLoading(false));
  }, [salarie, mois, annee, open]);

  const load = async () => {
    try {
      const [b, s] = await Promise.all([
        api.get("/chef/bulletins"),
        api.get("/chef/salaries"),
      ]);
      setItems(b.data);
      setSalaries(s.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  // Load history when salarie filter changes
  useEffect(() => {
    const params = historySalarie !== "all" ? { salarie_id: historySalarie } : {};
    api.get("/chef/bulletins/history", { params })
      .then((r) => setHistory(r.data))
      .catch(() => setHistory([]));
  }, [historySalarie, items.length]);

  const submit = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file || !salarie) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const params = new URLSearchParams({ salarie_id: salarie, mois: String(mois), annee: String(annee) });
      if (brut) params.append("brut", String(brut));
      await api.post(`/chef/bulletins?${params.toString()}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Bulletin déposé");
      setOpen(false);
      setBrut("");
      setBrutAutoFilled(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const download = async (id, name) => {
    try {
      const res = await api.get(`/files/bulletin/${id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Téléchargement impossible");
    }
  };

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Paie"
        title="Bulletins de paie"
        sub="Déposez les bulletins PDF mensuels. Chaque salarié retrouve ses bulletins dans son espace."
        right={
          <div className="flex gap-2 flex-wrap">
            <Button onClick={async () => {
              try {
                const r = await api.get("/chef/bulletins/export.csv", { responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
                const a = document.createElement("a");
                a.href = url; a.download = `bulletins-${new Date().toISOString().slice(0,10)}.csv`; a.click();
                window.URL.revokeObjectURL(url);
                toast.success("Export CSV téléchargé");
              } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
            }} data-testid="chef-export-bulletins-btn" variant="outline" className="h-11">
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Export CSV
            </Button>
            <Button onClick={() => setGenerateOpen(true)} data-testid="chef-generate-bulletin-btn" className="h-11 bg-[#0A0A0A] hover:bg-[#1F2937] text-white font-semibold">
              <FileSignature className="h-4 w-4 mr-2" /> Générer un bulletin
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="chef-upload-bulletin-btn" className="h-11 bg-[#002FA7] hover:bg-[#002175] text-white font-semibold">
                <Plus className="h-4 w-4 mr-2" /> Déposer un bulletin
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader><DialogTitle className="font-display text-2xl font-black tracking-tight">Nouveau bulletin</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="chef-bulletin-form">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Salarié</Label>
                  <Select value={salarie} onValueChange={setSalarie}>
                    <SelectTrigger data-testid="bulletin-salarie" className="h-11"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                    <SelectContent>
                      {salaries.map((s) => <SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Mois</Label>
                    <Select value={String(mois)} onValueChange={(v) => setMois(Number(v))}>
                      <SelectTrigger data-testid="bulletin-mois" className="h-11"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {MONTHS.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Année</Label>
                    <Select value={String(annee)} onValueChange={(v) => setAnnee(Number(v))}>
                      <SelectTrigger data-testid="bulletin-annee" className="h-11"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[2024, 2025, 2026, 2027].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Fichier PDF</Label>
                  <input ref={fileRef} type="file" accept="application/pdf" required data-testid="bulletin-file" className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-[#0A0A0A] file:text-white file:font-semibold" />
                </div>

                {/* Récapitulatif pré-paie automatique */}
                {salarie && (
                  <div className="border border-[#002FA7] bg-[#F8F9FA] p-4" data-testid="paie-preview">
                    <div className="flex items-center gap-2 mb-3">
                      <Calculator className="h-4 w-4 text-[#002FA7]" />
                      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002FA7]">Récapitulatif pré-paie</div>
                    </div>
                    {previewLoading ? (
                      <div className="text-sm text-[#868E96]">Calcul en cours…</div>
                    ) : preview ? (
                      <>
                        <div className="grid grid-cols-3 gap-3 mb-3">
                          <PreviewStat label="Heures travaillées" value={preview.total_heures.toFixed(2)} unit="h" testid="preview-heures" />
                          <PreviewStat label="Taux horaire" value={preview.taux_horaire.toFixed(2)} unit="€" testid="preview-taux" />
                          <PreviewStat label="Brut calculé" value={preview.brut_calcule.toFixed(2)} unit="€" highlight testid="preview-brut" />
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-[10px] uppercase tracking-wider text-[#868E96] border-t border-[#DEE2E6] pt-3">
                          <div>Jours travaillés: <span className="text-[#0A0A0A] font-bold">{preview.jours_travailles}</span></div>
                          <div>Congés payés: <span className="text-[#0A0A0A] font-bold">{preview.breakdown.jours_conges_paye}</span></div>
                          <div>Maladie: <span className="text-[#0A0A0A] font-bold">{preview.breakdown.jours_maladie}</span></div>
                          <div>Fériés: <span className="text-[#0A0A0A] font-bold">{preview.breakdown.jours_feries}</span></div>
                          <div>Repos: <span className="text-[#0A0A0A] font-bold">{preview.breakdown.jours_repos}</span></div>
                          <div>Sans solde: <span className="text-[#0A0A0A] font-bold">{preview.breakdown.jours_conge_sans_solde}</span></div>
                        </div>
                      </>
                    ) : (
                      <div className="text-sm text-[#868E96]">Aucune donnée de planning pour cette période.</div>
                    )}
                  </div>
                )}

                {/* Salaire brut modifiable (pré-rempli depuis le preview) */}
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold flex items-center justify-between">
                    <span>Salaire brut (€)</span>
                    {brutAutoFilled && (
                      <span className="flex items-center gap-1 text-[#002FA7] normal-case tracking-normal text-[10px] font-semibold">
                        <Sparkles className="h-3 w-3" /> Pré-rempli auto
                      </span>
                    )}
                  </Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={brut}
                    onChange={(e) => { setBrut(e.target.value); setBrutAutoFilled(false); }}
                    data-testid="bulletin-brut"
                    placeholder="Ex : 1850.00"
                    className="h-11 font-mono text-sm border-[#DEE2E6] focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20"
                  />
                  <div className="text-[10px] text-[#868E96]">Modifiable. Sera enregistré avec le bulletin et visible par le salarié.</div>
                </div>

                <DialogFooter>
                  <Button type="submit" disabled={uploading} data-testid="bulletin-submit" className="bg-[#002FA7] hover:bg-[#002175] text-white">
                    <Upload className="h-4 w-4 mr-2" /> {uploading ? "Envoi…" : "Déposer"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        }
      />
      <div className="p-6 lg:p-10 space-y-6">
        {/* Historique 12 mois avec sélecteur salarié */}
        <div className="space-y-3" data-testid="chef-history-section">
          <div className="flex items-center gap-3 flex-wrap">
            <Label className="text-xs uppercase tracking-[0.25em] font-semibold text-[#868E96]">Filtrer l'historique</Label>
            <Select value={historySalarie} onValueChange={setHistorySalarie}>
              <SelectTrigger data-testid="history-salarie-filter" className="w-72 h-10 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les salariés (cumul équipe)</SelectItem>
                {salaries.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom} · {s.poste}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <BulletinsHistoryChart
            data={history}
            title={historySalarie === "all" ? "Évolution masse salariale 12 mois" : "Historique 12 mois — " + (salaries.find((s) => s.id === historySalarie)?.prenom || "") + " " + (salaries.find((s) => s.id === historySalarie)?.nom || "")}
            subtitle="Cumul brut payé et net estimé par mois"
            testid="chef-history-chart"
          />
        </div>

        {/* Liste des bulletins */}
        <div className="bg-white border border-[#DEE2E6]" data-testid="chef-bulletins-list">
          <div className="grid grid-cols-12 gap-4 p-4 text-xs uppercase tracking-[0.2em] font-semibold bg-[#F8F9FA] border-b border-[#DEE2E6]">
            <div className="col-span-3">Salarié</div>
            <div className="col-span-2">Période</div>
            <div className="col-span-3">Fichier</div>
            <div className="col-span-1">Brut</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-1 text-right"></div>
          </div>
          {items.length === 0 ? (
            <div className="p-8 text-center text-sm text-[#868E96]">Aucun bulletin.</div>
          ) : items.map((b) => (
            <div key={b.id} className="grid grid-cols-12 gap-4 p-4 border-b border-[#DEE2E6] items-center text-sm" data-testid={`bulletin-row-${b.id}`}>
              <div className="col-span-3 font-semibold">{b.salarie ? `${b.salarie.prenom} ${b.salarie.nom}` : "—"}</div>
              <div className="col-span-2 font-mono text-xs">{MONTHS[b.mois - 1]} {b.annee}</div>
              <div className="col-span-3 truncate">{b.original_filename}</div>
              <div className="col-span-1 font-mono text-xs font-bold">{b.brut != null ? `${Number(b.brut).toFixed(2)} €` : "—"}</div>
              <div className="col-span-2 text-xs text-[#868E96]">{new Date(b.uploaded_at).toLocaleDateString("fr-FR")}</div>
              <div className="col-span-1 text-right">
                <Button size="sm" variant="outline" onClick={() => download(b.id, b.original_filename)} data-testid={`bulletin-download-${b.id}`}><Download className="h-3 w-3" /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <GenerateBulletinDialog
        open={generateOpen}
        onOpenChange={setGenerateOpen}
        salaries={salaries}
        onUploaded={load}
      />
    </div>
  );
}

function PreviewStat({ label, value, unit, highlight, testid }) {
  return (
    <div data-testid={testid} className={`p-3 border ${highlight ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-[#DEE2E6]"}`}>
      <div className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${highlight ? "opacity-80" : "text-[#868E96]"}`}>{label}</div>
      <div className="font-display text-xl font-black tracking-tight mt-1">
        {value}<span className={`text-sm ml-1 ${highlight ? "opacity-80" : "text-[#868E96]"} font-normal`}>{unit}</span>
      </div>
    </div>
  );
}
